import { useCallback, useEffect, useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { MarimoApi } from "@/api";
import { FormButton } from "@/components/form/FormButton";
import { FormInput } from "@/components/form/FormInput";
import { Icon } from "@/components/icons/Icon";
import { twMerge } from "@/utils/twMerge";

import type { DocumentModel } from "@/models/DocumentModel";

type PackageDependencyNode = MarimoApi.PackageDependencyNode;

function PackageRow({
  node,
  depth,
  removable,
  onRemove,
  submitting,
}: {
  node: PackageDependencyNode;
  depth: number;
  removable: boolean;
  onRemove: (name: string) => void;
  submitting: boolean;
}) {
  const t = useTranslations("sidebar.blocks.packages");
  const [expanded, setExpanded] = useState(false);
  const hasChildren = (node.dependencies?.length ?? 0) > 0;

  return (
    <>
      <li
        className="flex items-center gap-1 px-1 py-0.5 group"
        style={{ paddingLeft: 4 + depth * 12 }}
      >
        {hasChildren ? (
          <button
            type="button"
            aria-label={t(expanded ? "collapse" : "expand", { name: node.name })}
            aria-expanded={expanded}
            className="flex-shrink-0 hover:text-gray-700 dark:hover:text-gray-300"
            onClick={() => setExpanded((v) => !v)}
          >
            <Icon
              name="ChevronDown"
              width={12}
              height={12}
              className={twMerge("transition-transform", !expanded && "-rotate-90")}
            />
          </button>
        ) : (
          <span className="inline-block w-3 flex-shrink-0" aria-hidden />
        )}

        <span className="flex-1 truncate">
          <span>{node.name}</span>
          {node.version && <span className="text-gray-400">=={node.version}</span>}
        </span>

        {removable && (
          <button
            aria-label={t("remove", { name: node.name })}
            type="button"
            className={twMerge(
              "opacity-0 group-hover:opacity-100 transition-opacity",
              "hover:text-red-500 disabled:opacity-30",
            )}
            onClick={() => onRemove(node.name)}
            disabled={submitting}
          >
            <Icon name="Cross" width={14} height={14} />
          </button>
        )}
      </li>
      {expanded &&
        node.dependencies?.map((child) => (
          <PackageRow
            key={`${node.name}>${child.name}`}
            node={child}
            depth={depth + 1}
            removable={false}
            onRemove={onRemove}
            submitting={submitting}
          />
        ))}
    </>
  );
}

export const MarimoPackages = observer(function MarimoPackages({ doc }: { doc: DocumentModel }) {
  const t = useTranslations("sidebar.blocks.packages");
  const { sessionId, sessionStatus } = doc.marimo;
  const [tree, setTree] = useState<PackageDependencyNode | null>(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const authToken = await doc.marimo.getAuthToken();
      const res = await MarimoApi.marimoPackagesTree(
        sessionId,
        MarimoApi.getMarimoRequestConfig(authToken),
      );
      setTree(res?.tree ?? null);
    } finally {
      setLoading(false);
    }
  }, [sessionId, doc.marimo]);

  useEffect(() => {
    if (sessionStatus === "running") {
      refresh();
    }
  }, [sessionStatus, refresh]);

  if (!sessionId) {
    return <div className="text-xs text-gray-500 p-2">{t("noSession")}</div>;
  }

  const handleAdd = async () => {
    const pkg = input.trim();
    if (!pkg) return;
    setSubmitting(true);
    setError(null);
    try {
      const authToken = await doc.marimo.getAuthToken();
      const res = await MarimoApi.marimoPackagesAdd(
        sessionId,
        { package: pkg },
        MarimoApi.getMarimoRequestConfig(authToken),
      );
      if (res?.success) {
        setInput("");
        await refresh();
      } else {
        setError(res?.error ?? t("addFailed"));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t("addFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (name: string) => {
    setSubmitting(true);
    setError(null);
    try {
      const authToken = await doc.marimo.getAuthToken();
      const res = await MarimoApi.marimoPackagesRemove(
        sessionId,
        { package: name },
        MarimoApi.getMarimoRequestConfig(authToken),
      );
      if (res?.success) {
        await refresh();
      } else {
        setError(res?.error ?? t("removeFailed", { name }));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t("removeFailed", { name }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1.5 items-center">
        <div className="flex-1 min-w-0">
          <FormInput
            className="h-9 text-size-13"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !submitting && input.trim()) {
                e.preventDefault();
                handleAdd();
              }
            }}
            placeholder={t("addPlaceholder")}
            keepPlaceholderOnTop={false}
            disabled={submitting}
          />
        </div>
        <FormButton
          variant="accent"
          size="sm"
          className="h-9 px-3 flex-shrink-0"
          onClick={handleAdd}
          disabled={submitting || !input.trim()}
          isBusy={submitting}
        >
          {t("add")}
        </FormButton>
      </div>

      {error && <div className="text-xs text-red-500 pt-1 px-2">{error}</div>}

      <ul className="flex flex-col gap-0.5 text-size-13">
        {tree?.dependencies?.map((p) => (
          <PackageRow
            key={p.name}
            node={p}
            depth={0}
            removable
            onRemove={handleRemove}
            submitting={submitting}
          />
        ))}
      </ul>

      {loading && !tree && <div className="text-xs text-gray-400 pb-2 px-2">{t("loading")}</div>}
      {!loading && (!tree || (tree.dependencies?.length ?? 0) === 0) && (
        <div className="text-xs text-gray-400">{t("empty")}</div>
      )}
    </div>
  );
});
